/**
 * Nomes reais das RPC em supabase/migrations/20261017223000_st101_62_letter_share.sql.
 * O cliente não lê public.letters para preencher a carta pública.
 * Contrato: docs/recovery/st101-63/contrato-share.md
 */
export const GET_PUBLIC_LETTER = "get_public_letter";
export const LETTER_SHARE_ISSUE = "letter_share_issue";
export const LETTER_SHARE_REVOKE = "letter_share_revoke";

/** Data civil YYYY-MM-DD, o tipo date do SQL. Timestamptz não entra. */
const CIVIL_DATE = /^\d{4}-\d{2}-\d{2}$/;

const LEGACY_LETTER_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Link antigo /carta/<uuid>. O UUID não é credencial e não deve ir para a RPC. */
export function isLegacyLetterUuid(value: string): boolean {
  return LEGACY_LETTER_UUID.test(value.trim());
}

export type PublicLetter = {
  content: string;
  /** Data civil YYYY-MM-DD. O SQL devolve date, não timestamptz. */
  delivery_date: string;
};

export type PublicLetterView =
  | { status: "ready"; letter: PublicLetter }
  | { status: "unavailable" };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function soleRow(data: unknown): Record<string, unknown> | null {
  if (Array.isArray(data)) {
    if (data.length !== 1 || !isRecord(data[0])) return null;
    return data[0];
  }
  if (isRecord(data)) return data;
  return null;
}

/**
 * Aceita no máximo content e delivery_date.
 * delivery_date só entra como data civil YYYY-MM-DD, igual ao date do SQL.
 * Erro da RPC, lista vazia, várias linhas, timestamptz ou campos ausentes viram estado vazio.
 */
export function publicLetterFromRpc(read: {
  data: unknown;
  error: unknown;
}): PublicLetterView {
  if (read.error) return { status: "unavailable" };
  const row = soleRow(read.data);
  if (!row) return { status: "unavailable" };
  const content = row.content;
  const deliveryDate = row.delivery_date;
  if (typeof content !== "string" || typeof deliveryDate !== "string" || !CIVIL_DATE.test(deliveryDate)) {
    return { status: "unavailable" };
  }
  return {
    status: "ready",
    letter: {
      content,
      delivery_date: deliveryDate,
    },
  };
}

/** Recusa vazio, o próprio UUID da carta e qualquer valor no formato de UUID. */
export function shareTokenFromRpc(
  letterId: string,
  read: { data: unknown; error: unknown },
): string | null {
  if (read.error || typeof read.data !== "string") return null;
  const token = read.data.trim();
  if (!token || token === letterId || isLegacyLetterUuid(token)) return null;
  if (/[/?#\s]/.test(token)) return null;
  return token;
}

export function buildShareUrl(origin: string, token: string): string {
  const base = origin.replace(/\/$/, "");
  return `${base}/carta/${encodeURIComponent(token)}`;
}

/**
 * Texto que pode ir para a área de transferência.
 * Recusa o UUID da carta e qualquer token em formato de UUID.
 */
export function clipboardShareText(origin: string, letterId: string, token: string): string | null {
  if (!token || token === letterId || isLegacyLetterUuid(token)) return null;
  const url = buildShareUrl(origin, token);
  if (url.includes(letterId)) return null;
  return url;
}
